import {copyKeyFrame, KeyFrame} from '@/Animation/KeyFrame'
import {SlideTransition, TransitionType} from "@/Core/SlideTransition";
import {DagNode, DirectedAcyclicGraph} from "@/Core/Dag";

export interface SlideshowData {
    slides: SlideMetadata[];
    transitions: {[slideId: string]: SlideTransition};
    version: number;
}

export interface PlayableAudio {
    name: string;
    files: AudioFile[];
}

export interface AudioFile {
    src: string;
    fileType: string;
}

export interface SlideContentData {
    elements: SlideElement[];
    html: string;
    additionalData: {[x: string]: string};
    captions: Caption[];
    announcements: string[];
    voiceover: VoiceoverData|null;
}

export interface Caption {
    text: string;
    start: number;
    end: number;
    isReaderModeOnly: boolean;
    position: CaptionPosition;
}

export enum CaptionPosition {
    Top,
    Bottom
}

export interface SlideMetadata {
    id: string;
    type: SlideType;
    transition: SlideTransition;
    groupId: string|null;
    autoProceed: boolean;
    skipSlide: boolean;
}

export interface SlideData extends SlideContentData, SlideMetadata {

}

export enum SlideElementOrigin {
    TopLeft = "0 0",
    Centre = "50% 50%"
}

export interface SlideElementExisting {
    id: string;
    type: SlideElementType.Existing;
    origin: SlideElementOrigin|string;
    keyFrames: KeyFrame[];
}

export interface SlideElementImage {
    id: string;
    type: SlideElementType.Image;
    width?: number;
    height?: number;
    origin: SlideElementOrigin;
    keyFrames: KeyFrame[];
    src: string;
}

export interface SlideElementText {
    id: string;
    type: SlideElementType.Text;
    width?: number;
    height?: number;
    origin: SlideElementOrigin;
    text: string;
    weight?: number;
    fontSize: number;
    keyFrames: KeyFrame[];
}

export enum SlideElementType {
    Existing = 1, Image = 2, Text = 3
}

export type SlideElement = SlideElementExisting | SlideElementImage | SlideElementText;

export enum SlideType {
    Normal = 1,
    Title = 2,
    End = 3,
    UserFeedback = 4,
    TutorialResults = 5,
    Video = 6,

    MultipleChoice = 20,
    Sequence = 21,
    Pin = 22, // no longer supported
    Categorise = 23,
    Email = 24,
    Website = 25,
}

export interface VoiceoverData {
    id: string;
    startTime: number;
    endTime: number;
}

export function buildSlideGraph(slideData: SlideMetadata[]): DirectedAcyclicGraph<string> {
    let graph = new DirectedAcyclicGraph<string>();
    let nodeLookup = new Map<string, DagNode<string>>();

    for (let slide of slideData) {
        let node = new DagNode(slide.id);
        nodeLookup.set(slide.id, node);
        if (graph.root == null)
            graph.root = node;
    }

    for (let i = 0; i < slideData.length; i++) {
        let slide = slideData[i];
        let node = nodeLookup.get(slide.id);

        if (slide.transition.type === TransitionType.Choice) {
            for (let target of slide.transition.choices) {
                let targetNode = nodeLookup.get(target.id);
                if (targetNode == null) {
                    throw "Error in slide ["+slide.id+"]: could not find next slide [" + target.id + "]";
                }

                node!.addConnection(targetNode);
            }
        }
        else if (slide.transition.type === TransitionType.GoTo) {
            let target = slide.transition.id;
            let targetNode = nodeLookup.get(target);
            if (targetNode == null) {
                throw "Error in slide ["+slide.id+"]: could not find next slide [" + target + "]";
            }

            node!.addConnection(targetNode);
        }
        else if (slide.transition.type === TransitionType.Stateful) {
            for (let r of slide.transition.rules) {
                let target = r.id;
                let targetNode = nodeLookup.get(target);
                if (targetNode == null) {
                    throw "Error in slide ["+slide.id+"]: could not find next slide [" + target + "]";
                }

                node!.addConnection(targetNode);
            }
        }
    }

    return graph;
}

export function copyElement(element: SlideElementExisting): SlideElementExisting;
export function copyElement(element: SlideElementImage): SlideElementImage;
export function copyElement(element: SlideElementText): SlideElementText;
export function copyElement(element: SlideElement): SlideElement;
export function copyElement(element: SlideElement): SlideElement {
    if (element.type === SlideElementType.Image) {
        return copyImageElement(element);
    }
    else if (element.type === SlideElementType.Text) {
        return copyTextElement(element);
    }
    else if (element.type === SlideElementType.Existing) {
        return copyExistingElement(element);
    }

    throw "Unknown Element";
}

function copyImageElement(element: SlideElementImage): SlideElementImage {
    return {
        type: SlideElementType.Image,
        id: element.id,
        origin: element.origin,
        keyFrames: element.keyFrames.map(k => copyKeyFrame(k)),
        height: element.height,
        width: element.width,
        src: element.src
    };
}

function copyTextElement(element: SlideElementText): SlideElementText {
    return {
        type: SlideElementType.Text,
        id: element.id,
        origin: element.origin,
        keyFrames: element.keyFrames.map(k => copyKeyFrame(k)),
        text: element.text,
        height: element.height,
        width: element.width,
        fontSize: element.fontSize,
        weight: element.weight
    };
}

function copyExistingElement(element: SlideElementExisting): SlideElementExisting {
    return {
        type: SlideElementType.Existing,
        id: element.id,
        origin: element.origin,
        keyFrames: element.keyFrames.map(k => copyKeyFrame(k))
    };
}